import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { NotificationService } from './notification.service';
import { FeatureService } from './feature.service';

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp?: string;
}

export interface LoginRequest {
  usernameOrEmail: string;
  password: string;
}

export type UserType = 'ADMIN' | 'BUSINESS' | 'CUSTOMER' | 'APP' | 'PARTNER';

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string;
  atelierLibelle: string;
  atelierDescription?: string;
  atelierAdresse?: string;
  atelierCodePostale?: string;
  atelierNif?: string;
  atelierRccm?: string;
  typeUser?: UserType;
  locale: string;
  timezone: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  adresse?: string;
  countryCode: string;
  appUserStatus: string;
  avatarUrl: string;
  typeUser?: UserType;
  roles?: string[];
  createdBy?: string;
  createdAt?: string;
}

export type OtpScope =
  | 'AUTH'
  | 'REGISTRATION'
  | 'PAYMENT'
  | 'PASSWORD_RESET'
  | 'EMAIL_UPDATE'
  | 'PHONE_UPDATE'
  | 'TWO_FACTOR'
  | 'VERIFY_IDENTITY';

const TOKEN_KEY = 'dinki_token';
const USER_KEY = 'dinki_user';

/**
 * Délai (ms) avant l'expiration où l'utilisateur reçoit un toast d'avertissement.
 * 60 000 ms = 1 minute avant déconnexion automatique.
 */
const WARNING_BEFORE_EXPIRY_MS = 60_000;

const SKIP_TOAST = new HttpHeaders({ 'X-Skip-Error-Toast': 'true' });

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private notif = inject(NotificationService);
  private features = inject(FeatureService);
  private readonly base = `${environment.apiUrl}/x-auth`;

  private _user$ = new BehaviorSubject<AuthResponse | null>(this.storedUser());

  /** Timer de déconnexion automatique programmé sur l'expiration JWT. */
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;

  /** Timer d'avertissement pré-expiration. */
  private warningTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Si un token est déjà en localStorage au démarrage de l'app,
    // on (re)programme les timers selon son exp.
    if (this.getToken()) {
      this.scheduleAutoLogout();
    }

    // Synchronisation entre onglets : si un autre onglet se déconnecte ou
    // que le token est supprimé, on suit.
    window.addEventListener('storage', (e) => {
      if (e.key === TOKEN_KEY) {
        if (!e.newValue && this._user$.value) {
          this.clearSession();
          this.router.navigate(['/authentication/login']);
        } else if (e.newValue && e.newValue !== e.oldValue) {
          this.scheduleAutoLogout();
        }
      }
    });
  }

  get user$(): Observable<AuthResponse | null> {
    return this._user$.asObservable();
  }
  get currentUser(): AuthResponse | null {
    return this._user$.value;
  }
  get isLoggedIn(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  /** Type d'utilisateur courant (ADMIN, BUSINESS, CUSTOMER, APP, PARTNER) */
  currentRole(): UserType {
    const user = this.currentUser;
    if (user?.typeUser) return user.typeUser;
    const fromJwt = this.extractRoleFromJwt();
    if (fromJwt) return fromJwt;
    return 'BUSINESS';
  }

  hasRole(...roles: UserType[]): boolean {
    return roles.includes(this.currentRole());
  }

  /**
   * Page d'accueil par défaut selon le rôle. Utilisé après le login et par
   * le {@code roleGuard} pour rediriger un utilisateur qui essaie d'accéder
   * à une route non autorisée.
   */
  defaultRouteForRole(role: UserType = this.currentRole()): string {
    switch (role) {
      case 'PARTNER':
        return '/dinki/partenariat';
      case 'CUSTOMER':
        return '/dinki/dashboard'; // Dashboard router → CustomerDashboardComponent
      case 'ADMIN':
        return '/dinki/admin/overview';
      case 'BUSINESS':
      case 'APP':
      default:
        return '/dinki/dashboard';
    }
  }

  login(payload: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiResponse<AuthResponse>>(`${this.base}/login`, payload, {
        headers: SKIP_TOAST,
      })
      .pipe(
        map((res) => res.data),
        tap((user) => {
          if (user?.accessToken) {
            if (!user.typeUser) {
              const r = this.extractRoleFromJwt(user.accessToken);
              if (r) user.typeUser = r;
            }
            localStorage.setItem(TOKEN_KEY, user.accessToken);
            localStorage.setItem(USER_KEY, JSON.stringify(user));
            this._user$.next(user);
            this.scheduleAutoLogout();
          }
        }),
      );
  }

  register(payload: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiResponse<AuthResponse>>(`${this.base}/register`, payload, {
        headers: SKIP_TOAST,
      })
      .pipe(map((res) => res.data));
  }

  verifyAccount(
    destination: string,
    code: string,
  ): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.base}/verify-account`,
      { destination, code, scope: 'REGISTRATION' },
      { headers: SKIP_TOAST },
    );
  }

  requestOtp(
    destination: string,
    scope: OtpScope,
    channel: 'EMAIL' | 'SMS' = 'EMAIL',
  ): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/otp/request`, {
      destination,
      scope,
      channel,
    });
  }

  requestPasswordReset(
    usernameOrEmail: string,
  ): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.base}/reset_password/request`,
      { usernameOrEmail },
      { headers: SKIP_TOAST },
    );
  }

  verifyPasswordResetOtp(
    destination: string,
    code: string,
  ): Observable<ApiResponse<{ accessToken: string }>> {
    return this.http.post<ApiResponse<{ accessToken: string }>>(
      `${this.base}/reset_password/verify_otp`,
      {
        destination,
        code,
        scope: 'PASSWORD_RESET',
        channel: 'EMAIL',
      },
      { headers: SKIP_TOAST },
    );
  }

  confirmPasswordReset(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.base}/reset_password/update_password`,
      { token, newPassword, confirmPassword },
      { headers: SKIP_TOAST },
    );
  }

  verifyOtp(
    destination: string,
    code: string,
    scope: OtpScope,
  ): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/otp/verify`, {
      destination,
      code,
      scope,
    });
  }

  /**
   * Déconnexion utilisateur (manuelle).
   * Appelle le backend, nettoie le storage, annule les timers et redirige.
   */
  logout(reason?: string): void {
    if (this.getToken()) {
      this.http
        .post(`${this.base}/logout`, {}, { headers: SKIP_TOAST })
        .subscribe({ error: () => {} });
    }
    this.clearSession();
    this.router.navigate(['/authentication/login'], {
      queryParams: reason ? { reason } : undefined,
    });
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Vrai si le JWT courant est expiré (ou absent / illisible).
   * Bug d'unités corrigé : la claim `exp` est en SECONDES Unix, `Date.now()` en ms.
   */
  isTokenExpired(token: string | null = this.getToken()): boolean {
    if (!token) return true;
    const payload = this.decodeJwt(token);
    const exp = payload?.exp;
    if (typeof exp !== 'number') return false; // pas d'exp → on ne force pas
    const nowSec = Math.floor(Date.now() / 1000);
    return exp <= nowSec;
  }

  /** Millisecondes restantes avant expiration du JWT, ou 0 si déjà expiré. */
  msUntilExpiry(token: string | null = this.getToken()): number {
    if (!token) return 0;
    const payload = this.decodeJwt(token);
    if (typeof payload?.exp !== 'number') return 0;
    return Math.max(0, payload.exp * 1000 - Date.now());
  }

  // ─── Timers d'expiration ─────────────────────────────────────────────

  /**
   * (Re)programme le timer de déconnexion automatique et un avertissement
   * 1 minute avant. Appelée au login et au chargement de l'app si un token
   * est déjà présent.
   */
  private scheduleAutoLogout(): void {
    this.clearTimers();
    const remaining = this.msUntilExpiry();
    if (remaining <= 0) {
      // Token déjà expiré → déconnexion immédiate
      this.handleAutoLogout();
      return;
    }
    if (remaining > WARNING_BEFORE_EXPIRY_MS) {
      this.warningTimer = setTimeout(() => {
        this.notif.warning(
          'Votre session expirera dans 1 minute. Sauvegardez votre travail et reconnectez-vous.',
          'Session bientôt expirée',
        );
      }, remaining - WARNING_BEFORE_EXPIRY_MS);
    }
    this.logoutTimer = setTimeout(() => this.handleAutoLogout(), remaining);
  }

  private handleAutoLogout(): void {
    this.notif.error(
      'Votre session a expiré. Veuillez vous reconnecter.',
      'Session expirée',
    );
    this.logout('expired');
  }

  private clearTimers(): void {
    if (this.logoutTimer) clearTimeout(this.logoutTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    this.logoutTimer = null;
    this.warningTimer = null;
  }

  /** Nettoie la session sans appel backend (utile pour 401 / sync onglets). */
  private clearSession(): void {
    this.clearTimers();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._user$.next(null);
    // Purge le snapshot des features pour éviter qu'un autre utilisateur
    // hérite des droits du précédent sur la même session navigateur.
    this.features.clear();
  }

  private storedUser(): AuthResponse | null {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    } catch {
      return null;
    }
  }

  /** Décode le payload d'un JWT (sans vérification de signature). */
  private decodeJwt(token?: string | null): any | null {
    const t = token || this.getToken();
    if (!t) return null;
    try {
      const parts = t.split('.');
      if (parts.length < 2) return null;
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  private extractRoleFromJwt(token?: string): UserType | undefined {
    const payload = this.decodeJwt(token);
    if (!payload) return undefined;
    const candidates = [
      payload.typeUser,
      payload.type_user,
      payload.role,
      payload.userType,
      Array.isArray(payload.roles) ? payload.roles[0] : undefined,
      Array.isArray(payload.authorities)
        ? payload.authorities[0]?.replace?.(/^ROLE_/, '')
        : undefined,
    ].filter(Boolean);
    const normalized = (candidates[0] as string)
      ?.toString()
      ?.toUpperCase() as UserType;
    if (
      normalized === 'ADMIN' ||
      normalized === 'BUSINESS' ||
      normalized === 'CUSTOMER' ||
      normalized === 'APP' ||
      normalized === 'PARTNER'
    ) {
      return normalized;
    }
    return undefined;
  }
}
